import { useState } from 'react';
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink';
import { trackService, TrackUpdateRequest, uploadTrackImage } from 'data/services/trackService';
import { TrackDraft, Lesson } from '../../create/types';
import { showToast } from 'components/toast';
import { useVideoUpdate } from './useVideoUpdate';
import { videoProviderAllowInputUrl } from '../../create/components/trackLessonsFormCard/TrackLessonsFormCard';

export const useTrackEdit = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const navigate = useNavigate();
  const videoUpdate = useVideoUpdate();

  const handleUpdateFlow = async (
    trackId: string,
    draft: TrackDraft,
    lessons: Lesson[],
    trackStatus: 'Draft' | 'Publish',
    videoFiles?: { [lessonIndex: number]: File }
  ) => {
    try {
      setOverallProgress(0);
      setIsLoading(true);

      const trackDataToUpdate: TrackUpdateRequest = {
        id: trackId,
        title: draft.title.trim(),
        description: draft.description?.trim() || "",
        imageUrl: draft.imageUrl || "",
        status: trackStatus === 'Publish' ? 2 : 1,
        lessons: lessons.map((lesson, index) => {
          const videoProviderAllowUrl = videoProviderAllowInputUrl(lesson.videoProvider??"");
          const hasNewVideoFile = !!videoFiles && Object.prototype.hasOwnProperty.call(videoFiles, String(index)) && !!videoFiles[String(index)];
          const payload: any = {
            id: lesson.id ? String(lesson.id) : undefined,
            title: lesson.title.trim(),
            description: lesson.description?.trim() || "",
            videoId: lesson.videoProvider === lesson.originalvideoProvider?lesson.videoId: null,
            sequence: lesson.sequence || index + 1,
            videoUrl: videoProviderAllowUrl ? lesson.videoUrl: null,
            videoProvider: lesson.videoProvider,
            permissions: {
              allowFileUploads: lesson.allowFiles || false,
              allowQuestions: lesson.allowQuestions || false
            },
          };
          if (!hasNewVideoFile && typeof lesson.minutes === 'number' && lesson.minutes > 0) {
            payload.minutes = lesson.minutes;
          }
          return payload;
        }),
      };
      
      await trackService.updateTrack(trackDataToUpdate);

            if (draft.imageFile) {
        try {
          await uploadTrackImage(trackId, draft.imageFile);
        } catch (err) {
          showToast('error', 'Erro ao enviar imagem da trilha');
        }
      }

      setOverallProgress(50);

      let updatedTrack: any = null;
      try {
        updatedTrack = await trackService.getTrackDetails(trackId);
      } catch {}

      if (videoFiles && lessons && lessons.length > 0) {
        for (const [lessonIndex, file] of Object.entries(videoFiles)) {
          const idx = parseInt(lessonIndex);
          const original = lessons[idx];
          if (!original || !file) continue;

          const mapped = updatedTrack?.lessons?.find((l: any) => {
            const sameSeq = (l.sequence || idx + 1) === (original.sequence || idx + 1);
            return sameSeq;
          }) || updatedTrack?.lessons?.[idx] || original;

          const targetLessonId = String(mapped?.id || original.id || '');
          if (!targetLessonId) continue;

          try {
            await videoUpdate.uploadVideo(targetLessonId, file);
          } catch (error) {
          }
        }
      }
      
      setOverallProgress(100);

      showToast('success', 'Trilha atualizada com sucesso!');
      
      setTimeout(() => {
        navigate('/hub/tracks');
      }, 1000);
      
    } catch (error) {
      showToast('error', 'Erro ao atualizar trilha. Tente novamente.');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleUpdateFlow,
    isLoading: isLoading || videoUpdate.isUploading,
    uploadProgress: overallProgress || videoUpdate.uploadProgress,
    uploadError: videoUpdate.error,
    isUploading: videoUpdate.isUploading,
  };
};

